import React, { useMemo, useState } from "react";
import Dialog, { DialogProps } from "components/Dialog";
import { Radio, Text } from "@ui-kitten/components";
import Spacer from "components/Spacer";
import { View } from "react-native";
import Input from "components/Input";
import { formattedPhoneNumber } from "components/TextInput";
import useCurrentUser from "hooks/useCurrentUser";
import { useMutation } from "@tanstack/react-query";
import {
  checkPhoneVerifiedRequest,
  initiatePhoneVerifyRequest,
  updateUserFetcher,
  verifyPhoneRequest,
} from "queries/user.queries";
import toast from "utils/toast";
import Button from "components/Button";

const VerifyPhonePopup = (props: DialogProps) => {
  const { visible, ...restProps } = props;
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [phoneNumberCorrect, setPhoneNumberCorrect] = useState<boolean>(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  const { currentUser, updateCurrentUser } = useCurrentUser();
  const { mutateAsync: updateUser } = useMutation(updateUserFetcher);
  const { mutateAsync: checkPhoneVerified } = useMutation(
    checkPhoneVerifiedRequest
  );
  const { mutateAsync: initiatePhoneVerify } = useMutation(
    initiatePhoneVerifyRequest
  );
  const { mutateAsync: verifyPhone } = useMutation(verifyPhoneRequest);

  const isOkButtonDisabled = useMemo(() => {
    if (step === "phone") {
      return !phoneNumberCorrect && phoneNumber.length < 10;
    } else {
      return verificationCode.length < 6;
    }
  }, [step, phoneNumberCorrect, phoneNumber, verificationCode]);

  const [countdown, setCountdown] = useState(0);

  const beginCountdown = () => {
    setCountdown(60);
    const interval = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    // clear interval after 60 seconds
    setTimeout(() => {
      clearInterval(interval);
    }, 60000);
  };
  const handleOkPress = async () => {
    if (!currentUser) return;
    if (step === "phone") {
      if (!phoneNumberCorrect) {
        await updateUser({
          userId: currentUser!.id,
          user: {
            ...currentUser,
            phone: phoneNumber,
          },
        });
      }
      const phoneAlreadyVerified = await checkPhoneVerified({
        phone: phoneNumberCorrect ? currentUser.phone : phoneNumber,
      });
      if (phoneAlreadyVerified.value) {
        toast.error(
          "Phone number has already been used to verify an account, please enter a different number."
        );
        return;
      }

      initiatePhoneVerify({ username: currentUser!.username });
      beginCountdown();
      setStep("code");
    }
    if (step === "code") {
      const updatedUser = await verifyPhone({
        username: currentUser!.username,
        token: verificationCode,
        userId: currentUser!.id,
      });
      updateCurrentUser(updatedUser);
      setStep("phone");
      setPhoneNumberCorrect(true);
      setVerificationCode("");
      props.onCancel && props.onCancel();
      toast.success("Phone number verified successfully");
    }
  };
  return (
    <Dialog
      visible={visible}
      okText={
        step === "phone" ? "Send Verification Code" : "Verify Phone Number"
      }
      onOk={handleOkPress}
      okBtnProps={{
        disabled: isOkButtonDisabled,
      }}
      cancelText="Close"
      {...restProps}
    >
      <Text category="h5">We need to verify your phone number</Text>
      <Spacer />
      {step === "phone" && (
        <View>
          <Text>Is the phone number on your profile correct? </Text>
          <Text category="s2" style={{ marginVertical: 10 }}>
            {formattedPhoneNumber(currentUser?.phone || "")}
          </Text>

          <View style={{ flexDirection: "row" }}>
            <Radio
              checked={phoneNumberCorrect}
              onChange={() => setPhoneNumberCorrect(true)}
            >
              Yes
            </Radio>
            <Radio
              checked={!phoneNumberCorrect}
              onChange={() => setPhoneNumberCorrect(false)}
            >
              No
            </Radio>
          </View>

          {!phoneNumberCorrect && (
            <Input
              style={{ marginTop: 10 }}
              placeholder="Enter correct phone number"
              value={formattedPhoneNumber(phoneNumber)}
              onChangeText={(value) =>
                setPhoneNumber(value.replace(/[^0-9]/g, "").slice(0, 10))
              }
              keyboardType="number-pad"
            />
          )}

          <Text style={{ marginTop: 10 }}>
            We will send a verification code to the phone number listed above.
          </Text>
        </View>
      )}

      {step === "code" && (
        <View>
          <Text>
            Enter the verification code sent to{" "}
            {(phoneNumberCorrect ? currentUser?.phone : phoneNumber) as string}
          </Text>
          <View className="flex-row items-center justify-between mt-4">
            <Input
              placeholder="Verification Code"
              value={verificationCode}
              onChangeText={setVerificationCode}
              keyboardType="number-pad"
              style={{ flexGrow: 1 }}
            />
            <Button
              appearance="ghost"
              disabled={countdown > 0}
              onPress={() => {
                initiatePhoneVerify({ username: currentUser!.username });
                beginCountdown();
              }}
            >
              {`Resend ${countdown > 0 ? `(${countdown})` : ""}`}
            </Button>
          </View>
        </View>
      )}
      <Spacer />
      <Text category="c1" status="danger">
        *If you do not verify your phone number, you will not be able to make
        reservations. You can also verify your phone number later on the Account
        page.
      </Text>
    </Dialog>
  );
};

export default VerifyPhonePopup;
